import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { TranslocoService } from "@ngneat/transloco";
import { OrganizationBrandingService } from "app/core/branding/branding.service";
import { User } from "app/core/user/user.types";
import { UserProfileService } from "app/core/user/user-profile.service";
import { UserService } from "app/core/user/user.service";
import { catchError, defer, forkJoin, Observable, of, switchMap, tap, throwError } from "rxjs";
import { SessionManagerService } from "./session-manager.service";

export interface LoginResponse {
    // Present for regular users (2FA required)
    user_id?: number;
    message?: string;
    // Present for super-admin (direct login, no 2FA)
    status?: string;
    token?: string;
    user?: any;
}

export interface ValidateResponse {
    token: string;
    user: any;
}

export interface ResendResponse {
    message: string;
}

export interface ValidateTokensResponse {
    results: Record<string, { valid: boolean; user_id?: number; email?: string; name?: string; role?: string; organization?: { id: string; name: string } }>;
}

@Injectable({ providedIn: "root" })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private _userProfileService = inject(UserProfileService);
    private _brandingService = inject(OrganizationBrandingService);
    private _translocoService = inject(TranslocoService);
    private _sessionManager = inject(SessionManagerService);
    private _router = inject(Router);
    private _lastCheckTime: number | null = null;

    // 2FA temporary data
    private _pendingUserId: number | null = null;
    private _pendingEmail: string | null = null;

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get accessToken(): string {
        return localStorage.getItem("accessToken") ?? "";
    }

    set accessToken(token: string) {
        localStorage.setItem("accessToken", token);
    }

    get pendingUserId(): number | null {
        return this._pendingUserId;
    }

    get pendingEmail(): string | null {
        return this._pendingEmail;
    }

    clearPending2FAData(): void {
        this._pendingUserId = null;
        this._pendingEmail = null;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post("api/password/forgot", { email });
    }

    validateResetPasswordToken(email: string, token: string): Observable<any> {
        return this._httpClient.post("api/password/validate", { email, token });
    }

    resetPassword(password: { password: string; password_confirmation: string; token: string; email: string }): Observable<any> {
        return this._httpClient.post("api/password/reset", password);
    }

    /**
     * Sign in - Step 1 of 2FA (or direct login for super-admin).
     * Supports multi-session: does NOT block if already authenticated on another account.
     */
    signIn(credentials: { email: string; password: string }): Observable<LoginResponse> {
        return this._httpClient.post<LoginResponse>("api/v2/auth/login", credentials).pipe(
            tap((response: LoginResponse) => {
                if (response.token) {
                    // Super admin - direct login without 2FA
                    const userId = Number(response.user?.id);
                    this._sessionManager.addOrUpdateSession(userId, response.token, response.user);
                    this._sessionManager.switchTo(userId);
                    this._authenticated = true;
                    this._userService.user = response.user;
                    this._syncPreferredLanguage(response.user);
                    this._brandingService.loadBranding().subscribe();
                } else if (response.user_id) {
                    // Regular user - store pending 2FA data
                    this._pendingUserId = response.user_id;
                    this._pendingEmail = credentials.email;
                }
            })
        );
    }

    /**
     * Validate 2FA code - Step 2 of 2FA.
     * Registers the new session in the multi-session manager.
     */
    validate2FACode(code: string): Observable<ValidateResponse> {
        if (!this._pendingUserId) {
            return throwError(() => new Error("No pending 2FA validation. Please sign in first."));
        }

        return this._httpClient
            .post<ValidateResponse>("api/v2/auth/validate", {
                user_id: this._pendingUserId,
                code: code,
            })
            .pipe(
                tap((response: ValidateResponse) => {
                    const userId = Number(response.user?.id);

                    // Register in multi-session manager
                    this._sessionManager.addOrUpdateSession(userId, response.token, response.user);
                    this._sessionManager.switchTo(userId);

                    // Legacy compat
                    this._authenticated = true;
                    this._userService.user = response.user;
                    this._syncPreferredLanguage(response.user);
                    this.clearPending2FAData();
                    this._brandingService.loadBranding().subscribe();
                })
            );
    }

    resend2FACode(): Observable<ResendResponse> {
        if (!this._pendingUserId) {
            return throwError(() => new Error("No pending 2FA validation. Please sign in first."));
        }

        return this._httpClient.post<ResendResponse>("api/v2/auth/resend", {
            user_id: this._pendingUserId,
        });
    }

    signInUsingToken(): Observable<any> {
        return this._httpClient
            .post("api/auth/sign-in-with-token", {
                accessToken: this.accessToken,
            })
            .pipe(
                catchError(() => of(false)),
                switchMap((response: any) => {
                    if (response.token) {
                        this.accessToken = response.token;
                    }
                    this._authenticated = true;
                    this._userService.user = response.user;
                    this._syncPreferredLanguage(response.user);
                    return of(true);
                })
            );
    }

    private _syncPreferredLanguage(user: User | null): void {
        const translocoLang = this._userProfileService.toTranslocoLang(user?.preferred_language);
        if (translocoLang && user?.preferred_language) {
            this._userProfileService.setPreferredLanguageInStorage(user.preferred_language);
            this._translocoService.setActiveLang(translocoLang);
        }
    }

    /**
     * Sign out a specific account or the currently active account.
     * If other sessions remain, switches to the most recent one.
     */
    signOut(userId?: number): Observable<any> {
        const targetUserId = userId ?? this._sessionManager.getActiveUserId();
        const targetToken = targetUserId
            ? this._sessionManager.getTokenForAccount(targetUserId)
            : this.accessToken;

        // Build the logout request with the target account's token
        const headers = targetToken
            ? new HttpHeaders({ Authorization: `Bearer ${targetToken}` })
            : undefined;

        return defer(() =>
            this._httpClient.post("api/auth/logout", undefined, { headers })
        ).pipe(
            catchError(() => of(false)),
            switchMap(() => of(true)),
            tap(() => {
                if (targetUserId) {
                    this._sessionManager.removeSession(targetUserId);
                }

                // If there are remaining sessions, the SessionManager already switched to the next one
                const remaining = this._sessionManager.getAll();
                if (remaining.length > 0) {
                    this._authenticated = true;
                    // Reload user from the newly active session
                    const activeUser = this._sessionManager.getActive();
                    if (activeUser) {
                        const user = this._sessionManager.getUserForAccount(activeUser.userId);
                        if (user) {
                            this._userService.user = user;
                        }
                    }
                } else {
                    this._authenticated = false;
                }
            })
        );
    }

    /**
     * Sign out of all accounts.
     */
    signOutAll(): Observable<any> {
        const sessions = this._sessionManager.getAll();

        if (sessions.length === 0) {
            this._authenticated = false;
            return of(true);
        }

        // Send logout request for each session token
        const logoutRequests = sessions.map((session) => {
            const token = this._sessionManager.getTokenForAccount(session.userId);
            const headers = token
                ? new HttpHeaders({ Authorization: `Bearer ${token}` })
                : undefined;

            return this._httpClient
                .post("api/auth/logout", undefined, { headers })
                .pipe(catchError(() => of(false)));
        });

        return forkJoin(logoutRequests).pipe(
            tap(() => {
                this._sessionManager.removeAllSessions();
                this._authenticated = false;
            })
        );
    }

    /**
     * Validate multiple tokens at once (for multi-session health check).
     */
    validateTokens(tokens: string[]): Observable<ValidateTokensResponse> {
        return this._httpClient.post<ValidateTokensResponse>(
            "api/v2/auth/validate-tokens",
            { tokens }
        );
    }

    /**
     * Log an account switch for audit purposes.
     */
    logAccountSwitch(fromUserId: number): Observable<any> {
        return this._httpClient.post("api/v2/auth/log-switch", {
            from_user_id: fromUserId,
        }).pipe(catchError(() => of(false)));
    }

    signUp(user: any): Observable<any> {
        return this._httpClient.post("api/auth/sign-up", user);
    }

    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post("api/auth/unlock-session", credentials);
    }

    /**
     * Check the authentication status.
     */
    check(): Observable<boolean> {
        if (this._authenticated) {
            return of(true);
        }

        if (!this.accessToken) {
            return of(false);
        }

        return this._checkToken();
    }

    private _checkToken(): Observable<boolean> {
        const now: number = Date.now();
        const minutes: number = 1;

        if (this._lastCheckTime && now - this._lastCheckTime < minutes * 60 * 1000) {
            return of(true);
        } else {
            this._lastCheckTime = now;

            return defer(() => this._httpClient.get("api/auth/check")).pipe(
                switchMap(() => {
                    this._authenticated = true;
                    return of(true);
                }),
                catchError((error: HttpErrorResponse) => {
                    // Only a rejected token ends the session. A 429 (rate limit), 5xx or a network
                    // failure says nothing about the token, so keep the user in; the next real
                    // request surfaces the problem. Note the interceptors only close the session for
                    // some URLs (a 401 from api/auth/check or campaign endpoints reaches here as an
                    // ApiError and doesn't), so this check is what reports a dead token.
                    const tokenRejected = error?.status === 401 || error?.status === 403;
                    if (tokenRejected) {
                        // A rejection is not a successful check: don't let the 1-minute cache answer
                        // true for a token that no longer works.
                        this._lastCheckTime = null;
                    }
                    return of(!tokenRejected);
                })
            );
        }
    }
}
